/**
 * ListingPreviewPage — the landlord's draft preview (US-111, BR-108, BR-109).
 *
 * Route: /leasing/listings/:id/preview, under ProtectedRoute but OUTSIDE the app shell.
 *
 * The page body is the shared prospect view (@resida/listing-view) rendered in the always-light
 * prospect theme, fed by the draft preview endpoint (saved data only; empty sections absent, no
 * contact email yet). The Schedule and Apply actions are visibly disabled and do nothing. Beside it,
 * a "Needed before go-live" panel reads each item's state from the same guide state the rail uses;
 * below the laptop breakpoint that panel is a disclosure under the banner.
 *
 * States: loading skeleton, error with Retry (role="alert"), not found (a listing that is not a
 * draft, or one the caller cannot view, reads identically) with a link back to Listings.
 */

import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Info } from 'lucide-react';
import { CTA_CLASSES, CTA_LABELS, ListingCtaBar, ListingView, ProspectShell } from '@resida/listing-view';
import { Badge } from '@/components/ui/badge';
import { Disclosure } from '@/components/layout/Disclosure';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { fetchListing } from '../../lib/leasing.api';
import { useGuideState, type GuideState } from './listing-guide/useGuideState';
import type { GuideStepKey } from './listing-guide/guide-steps';
import { useListingPreview } from './useListingPreview';

/** Same breakpoint the guide shell uses for its rail. */
const LAPTOP_QUERY = '(min-width: 1024px)';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

// ---------------------------------------------------------------------------
// Readiness panel
// ---------------------------------------------------------------------------

interface ReadinessRow {
  key: string;
  label: string;
  step: GuideStepKey;
  done: boolean;
  optional?: boolean;
  loading: boolean;
}

function readinessRows(guide: GuideState, photoCount: number): ReadinessRow[] {
  const marketingLoading = guide.steps.marketing.loading;
  return [
    {
      key: 'description',
      label: 'Description',
      step: 'marketing',
      done: !guide.marketing.missing.includes('Description'),
      loading: marketingLoading,
    },
    {
      key: 'photos',
      label: photoCount > 0 ? `Photos (${photoCount})` : 'Photos',
      step: 'marketing',
      done: !guide.marketing.missing.includes('Photos'),
      loading: marketingLoading,
    },
    {
      key: 'details',
      label: 'Rent and terms',
      step: 'details',
      done: guide.done.details,
      loading: guide.steps.details.loading,
    },
    {
      key: 'showings',
      label: 'Showing times and open houses',
      step: 'showings',
      done: guide.done.showings,
      loading: guide.steps.showings.loading,
    },
    {
      key: 'contact',
      label: 'Contact name and phone',
      step: 'contact',
      done: guide.done.contact,
      loading: guide.steps.contact.loading,
    },
    {
      key: 'agent',
      label: 'AI leasing agent',
      step: 'agent',
      done: guide.done.agent,
      loading: guide.steps.agent.loading,
    },
    {
      key: 'screening',
      label: 'Screening criteria',
      step: 'contact',
      done: guide.screening.status === 'done',
      optional: true,
      loading: guide.screening.loading,
    },
  ];
}

function RowBadge({ row }: { row: ReadinessRow }) {
  if (row.loading) return <Skeleton className="h-5 w-16" aria-hidden="true" />;
  if (row.done) return <Badge variant="success">Done</Badge>;
  if (row.optional) return <Badge variant="secondary">Optional</Badge>;
  return <Badge variant="warning">Needs attention</Badge>;
}

function ReadinessList({ listingId, rows }: { listingId: string; rows: ReadinessRow[] }) {
  return (
    <>
      <ul className="m-0 list-none p-0" data-testid="preview-readiness-list">
        {rows.map((row) => (
          <li
            key={row.key}
            data-testid={`preview-readiness-row-${row.key}`}
            className="flex min-h-[44px] items-center gap-3 border-b border-border py-2 last:border-b-0"
          >
            <RowBadge row={row} />
            <span className="flex-1 text-sm text-foreground">{row.label}</span>
            <Link
              to={`/leasing/listings/${listingId}/setup/${row.step}`}
              aria-label={`${row.optional && !row.done ? 'Add' : 'Edit'} ${row.label}`}
              data-testid={`preview-readiness-edit-${row.key}`}
              className={cn(
                'inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md px-2 text-sm font-medium text-primary hover:underline',
                focusRing
              )}
            >
              {row.optional && !row.done ? 'Add' : 'Edit'}
            </Link>
          </li>
        ))}
      </ul>
      <p
        data-testid="preview-readiness-note"
        className="mt-4 flex items-start gap-2 rounded-md border border-info/25 bg-info/10 p-3 text-xs text-foreground"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
        Your listing email and public link are created when you publish, so the email is not shown here yet.
      </p>
    </>
  );
}

function ReadinessPanel({ listingId, rows, isLaptop }: { listingId: string; rows: ReadinessRow[]; isLaptop: boolean }) {
  const needed = rows.filter((row) => !row.optional);
  const doneCount = needed.filter((row) => row.done).length;

  if (isLaptop) {
    return (
      <aside
        aria-labelledby="preview-readiness-heading"
        data-testid="preview-readiness"
        className="sticky top-4 self-start rounded-xl border border-border bg-card p-5"
      >
        <h2 id="preview-readiness-heading" className="text-base font-semibold text-foreground">
          Needed before go-live
        </h2>
        <p className="mb-2 text-xs text-muted-foreground">Everything here is read from what you have saved.</p>
        <ReadinessList listingId={listingId} rows={rows} />
      </aside>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card px-4">
      <Disclosure
        title="Needed before go-live"
        meta={`${doneCount} of ${needed.length} done`}
        testId="preview-readiness"
        headingLevel={2}
      >
        <p className="mb-2 text-xs text-muted-foreground">Everything here is read from what you have saved.</p>
        <ReadinessList listingId={listingId} rows={rows} />
      </Disclosure>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page chrome and states
// ---------------------------------------------------------------------------

/** Keeps search engines away while the page is mounted (the endpoint also sends X-Robots-Tag). */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
}

function PreviewBanner({ listingId, setupStep }: { listingId: string; setupStep: GuideStepKey }) {
  return (
    <div
      role="region"
      aria-label="Preview"
      data-testid="preview-banner"
      className="border-b border-info/25 bg-info/10"
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3">
        <Badge variant="info">Preview</Badge>
        <p className="min-w-0 flex-1 text-sm text-muted-foreground">
          <strong className="font-semibold text-foreground">This is how prospects will see your listing.</strong>{' '}
          It is not live yet, and only you can see this page.
        </p>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <Button asChild variant="outline" className="min-h-[44px] flex-1 sm:flex-none">
            <Link to={`/leasing/listings/${listingId}/setup/${setupStep}`} data-testid="preview-back-to-setup">
              <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
              Back to setup
            </Link>
          </Button>
          <Button asChild className="min-h-[44px] flex-1 sm:flex-none">
            <Link to={`/leasing/listings/${listingId}/setup/review`} data-testid="preview-continue-to-review">
              Continue to review
              <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function PreviewLoading() {
  return (
    <div data-testid="preview-loading" role="status" aria-label="Loading preview" className="min-h-screen bg-background">
      <Skeleton className="h-16 w-full rounded-none" />
      <div className="mx-auto mt-6 grid max-w-6xl gap-6 px-4 lg:grid-cols-[minmax(0,720px)_320px] lg:justify-center">
        <div className="space-y-4">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="aspect-[4/3] w-full sm:aspect-video" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
        <Skeleton className="hidden h-96 w-full lg:block" />
      </div>
    </div>
  );
}

function PreviewMessage({ testId, children }: { testId: string; children: React.ReactNode }) {
  return (
    <div data-testid="preview-page" className="flex min-h-screen items-center justify-center bg-background px-4">
      <div
        data-testid={testId}
        className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center"
      >
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ListingPreviewPage() {
  const { id = '' } = useParams<{ id: string }>();
  useNoIndex();
  const isLaptop = useMediaQuery(LAPTOP_QUERY);

  const preview = useListingPreview(id);
  const detail = useQuery({
    queryKey: ['listing', id],
    queryFn: () => fetchListing(id),
    enabled: id !== '',
  });
  const listing = detail.data?.listing ?? null;
  const guide = useGuideState({ unitId: listing?.unitId ?? '', listing });

  const previewStatus = (preview.error as { status?: number } | null)?.status;
  const detailStatus = (detail.error as { status?: number } | null)?.status;

  if (previewStatus === 404 || detailStatus === 404) {
    return (
      <PreviewMessage testId="preview-not-found">
        <h1 className="mb-2 text-xl font-semibold text-foreground">Preview not available</h1>
        <p className="mb-5 text-sm text-muted-foreground">
          Only a listing that is still a draft can be previewed, and only by people who can view it.
        </p>
        <Button asChild className="min-h-[44px]">
          <Link to="/leasing/listings" data-testid="preview-not-found-link">
            Go to Listings
          </Link>
        </Button>
      </PreviewMessage>
    );
  }

  if (preview.error || detail.error) {
    return (
      <PreviewMessage testId="preview-error">
        <div role="alert">
          <h1 className="mb-2 text-xl font-semibold text-foreground">We could not load the preview</h1>
          <p className="mb-5 text-sm text-muted-foreground">Check your connection and try again.</p>
        </div>
        <Button
          variant="outline"
          className="min-h-[44px]"
          data-testid="preview-retry"
          onClick={() => {
            void preview.refetch();
            void detail.refetch();
          }}
        >
          Retry
        </Button>
      </PreviewMessage>
    );
  }

  if (!preview.data || !listing) return <PreviewLoading />;

  const rows = readinessRows(guide, preview.data.photos.length);
  const setupStep: GuideStepKey = guide.firstIncomplete;

  // The same actions as the public page (shared wording and styling), visibly disabled and doing nothing.
  const ctaBar = (
    <ListingCtaBar rentCents={preview.data.askingRentCents}>
      <button
        type="button"
        disabled
        aria-disabled="true"
        data-testid="preview-schedule-showing-cta"
        className={`${CTA_CLASSES.schedule} ${CTA_CLASSES.inert}`}
      >
        {CTA_LABELS.schedule}
      </button>
      <button
        type="button"
        disabled
        aria-disabled="true"
        data-testid="preview-apply-cta"
        className={`${CTA_CLASSES.apply} ${CTA_CLASSES.inert}`}
      >
        {CTA_LABELS.apply}
      </button>
    </ListingCtaBar>
  );

  return (
    <div data-testid="preview-page" className="min-h-screen bg-background text-foreground">
      <PreviewBanner listingId={id} setupStep={setupStep} />
      <div className="mx-auto max-w-6xl px-4 py-6">
        {!isLaptop && (
          <div className="mb-4">
            <ReadinessPanel listingId={id} rows={rows} isLaptop={false} />
          </div>
        )}
        <div className="grid gap-8 lg:grid-cols-[minmax(0,720px)_320px] lg:justify-center">
          <div
            data-testid="preview-frame"
            className="prospect-theme min-w-0 overflow-clip rounded-xl border border-border [&>[data-testid=prospect-shell]]:min-h-0"
          >
            <ProspectShell>
              <ListingView listing={preview.data} ctaBar={ctaBar} />
            </ProspectShell>
          </div>
          {isLaptop && <ReadinessPanel listingId={id} rows={rows} isLaptop />}
        </div>
      </div>
    </div>
  );
}
